import produce from 'immer';
import { IBrand, IBuzzBridgeProfileResponse, IInfluencer, types } from './types';

// Interface for BuzzBridge state
export interface IBuzzBridgeInitialState {
    buzzProfile?: IBuzzBridgeProfileResponse | null;
    gettingBuzzProfile?: boolean;
    // Program state
    joiningProgram: boolean;
    programJoined: boolean;
    programError: any;

    // Campaigns state
    campaigns: any[];
    currentCampaign: any;
    creatingCampaign: boolean;
    updatingCampaign: boolean;
    deletingCampaign: boolean;
    gettingCampaignDetails: boolean;
    campaignError: any;

    // Campaign jobs state
    campaignJobs: any[];
    gettingCampaignJobs: boolean;
    campaignJobsError: any;

    // Managed campaigns state
    managedCampaigns: any[];
    gettingManagedCampaigns: boolean;
    managedCampaignsError: any;

    // Brand management state
    brands: IBrand[];
    gettingBrands: boolean;
    addingBrand: boolean;
    updatingBrand: boolean;
    deletingBrand: boolean;
    brandError: any;

    // Join requests state
    joinRequests: any[];
    requestingToJoin: boolean;
    approvingRequest: boolean;
    rejectingRequest: boolean;
    joinRequestError: any;

    // Messages state
    messages: any[];
    sendingMessage: boolean;
    deletingMessage: boolean;
    updatingMessage: boolean;
    messageError: any;

    // Deliverables state
    deliverables: any[];
    requestingUpdate: boolean;
    approvingDeliverable: boolean;
    uploadingDeliverable: boolean;
    deliverableError: any;

    // Post scheduling state
    scheduledPosts: any[];
    schedulingPost: boolean;
    scheduleError: any;

    // General loading states
    loading: boolean;
    error: any;
}

const initialState: IBuzzBridgeInitialState = {
    // currentUserProfile
    buzzProfile: null,
    // Program state
    joiningProgram: false,
    programJoined: false,
    programError: null,

    // Campaigns state
    campaigns: [],
    currentCampaign: null,
    creatingCampaign: false,
    updatingCampaign: false,
    deletingCampaign: false,
    gettingCampaignDetails: false,
    campaignError: null,

    // Campaign jobs state
    campaignJobs: [],
    gettingCampaignJobs: false,
    campaignJobsError: null,

    // Managed campaigns state
    managedCampaigns: [],
    gettingManagedCampaigns: false,
    managedCampaignsError: null,

    // Brand management state
    brands: [],
    gettingBrands: false,
    addingBrand: false,
    updatingBrand: false,
    deletingBrand: false,
    brandError: null,

    // Join requests state
    joinRequests: [],
    requestingToJoin: false,
    approvingRequest: false,
    rejectingRequest: false,
    joinRequestError: null,

    // Messages state
    messages: [],
    sendingMessage: false,
    deletingMessage: false,
    updatingMessage: false,
    messageError: null,

    // Deliverables state
    deliverables: [],
    requestingUpdate: false,
    approvingDeliverable: false,
    uploadingDeliverable: false,
    deliverableError: null,

    // Post scheduling state
    scheduledPosts: [],
    schedulingPost: false,
    scheduleError: null,

    // General loading states
    loading: false,
    error: null,
};

const reducer = (state = initialState, action: any) => {
    const { type, payload } = action;
    switch (type) {
        // Program actions
        case types.JOIN_PROGRAM:
            return produce(state, draftState => {
                draftState.joiningProgram = true;
                draftState.programError = null;
            });
        case types.JOIN_PROGRAM_SUCCESS:
            return produce(state, draftState => {
                draftState.joiningProgram = false;
                draftState.programJoined = true;
            });
        case types.JOIN_PROGRAM_FAIL:
            return produce(state, draftState => {
                draftState.joiningProgram = false;
                draftState.programError = payload;
            });

        // Campaign actions
        case types.CREATE_OFFSITE_CAMPAIGN:
            return produce(state, draftState => {
                draftState.creatingCampaign = true;
                draftState.campaignError = null;
            });
        case types.CREATE_OFFSITE_CAMPAIGN_SUCCESS:
            return produce(state, draftState => {
                draftState.creatingCampaign = false;
                draftState.campaigns = [...draftState.campaigns, payload];
            });
        case types.CREATE_OFFSITE_CAMPAIGN_FAIL:
            return produce(state, draftState => {
                draftState.creatingCampaign = false;
                draftState.campaignError = payload;
            });

        case types.GET_OFFSITE_CAMPAIGN_DETAILS:
            return produce(state, draftState => {
                draftState.gettingCampaignDetails = true;
                draftState.campaignError = null;
            });
        case types.GET_OFFSITE_CAMPAIGN_DETAILS_SUCCESS:
            return produce(state, draftState => {
                draftState.gettingCampaignDetails = false;
                draftState.currentCampaign = payload;
            });
        case types.GET_OFFSITE_CAMPAIGN_DETAILS_FAIL:
            return produce(state, draftState => {
                draftState.gettingCampaignDetails = false;
                draftState.campaignError = payload;
            });

        case types.GET_CAMPAIGN_JOB_DETAILS:
            return produce(state, draftState => {
                draftState.gettingCampaignJobs = true;
                draftState.campaignJobsError = null;
            });
        case types.GET_CAMPAIGN_JOB_DETAILS_SUCCESS:
            return produce(state, draftState => {
                draftState.gettingCampaignJobs = false;
                draftState.campaignJobs = payload;
            });
        case types.GET_CAMPAIGN_JOB_DETAILS_FAIL:
            return produce(state, draftState => {
                draftState.gettingCampaignJobs = false;
                draftState.campaignJobsError = payload;
            });

        case types.MANAGE_OFFSITE_CAMPAIGNS:
            return produce(state, draftState => {
                draftState.gettingManagedCampaigns = true;
                draftState.managedCampaignsError = null;
            });
        case types.MANAGE_OFFSITE_CAMPAIGNS_SUCCESS:
            return produce(state, draftState => {
                draftState.gettingManagedCampaigns = false;
                draftState.managedCampaigns = payload;
            });
        case types.MANAGE_OFFSITE_CAMPAIGNS_FAIL:
            return produce(state, draftState => {
                draftState.gettingManagedCampaigns = false;
                draftState.managedCampaignsError = payload;
            });

        case types.UPDATE_OFFSITE_CAMPAIGN:
            return produce(state, draftState => {
                draftState.updatingCampaign = true;
                draftState.campaignError = null;
            });
        case types.UPDATE_OFFSITE_CAMPAIGN_SUCCESS:
            return produce(state, draftState => {
                draftState.updatingCampaign = false;
                draftState.campaigns = draftState.campaigns.map(campaign =>
                    campaign.id === payload.id ? payload : campaign,
                );
                if (draftState.currentCampaign?.id === payload.id) {
                    draftState.currentCampaign = payload;
                }
            });
        case types.UPDATE_OFFSITE_CAMPAIGN_FAIL:
            return produce(state, draftState => {
                draftState.updatingCampaign = false;
                draftState.campaignError = payload;
            });

        case types.DELETE_OFFSITE_CAMPAIGN:
            return produce(state, draftState => {
                draftState.deletingCampaign = true;
                draftState.campaignError = null;
            });
        case types.DELETE_OFFSITE_CAMPAIGN_SUCCESS:
            return produce(state, draftState => {
                draftState.deletingCampaign = false;
                draftState.campaigns = draftState.campaigns.filter(campaign => campaign.id !== payload.campaignId);
                if (draftState.currentCampaign?.id === payload.campaignId) {
                    draftState.currentCampaign = null;
                }
            });
        case types.DELETE_OFFSITE_CAMPAIGN_FAIL:
            return produce(state, draftState => {
                draftState.deletingCampaign = false;
                draftState.campaignError = payload;
            });

        // Brand management actions
        case types.GET_BRANDS:
            return produce(state, draftState => {
                draftState.gettingBrands = true;
                draftState.brandError = null;
            });
        case types.GET_BRANDS_SUCCESS:
            return produce(state, draftState => {
                draftState.gettingBrands = false;
                draftState.brands = payload;
            });
        case types.GET_BRANDS_FAIL:
            return produce(state, draftState => {
                draftState.gettingBrands = false;
                draftState.brandError = payload;
            });

        case types.ADD_BRAND:
            return produce(state, draftState => {
                draftState.addingBrand = true;
                draftState.brandError = null;
            });
        case types.ADD_BRAND_SUCCESS:
            return produce(state, draftState => {
                draftState.addingBrand = false;
                draftState.brands = [...draftState.brands, payload];
            });
        case types.ADD_BRAND_FAIL:
            return produce(state, draftState => {
                draftState.addingBrand = false;
                draftState.brandError = payload;
            });

        case types.UPDATE_BRAND:
            return produce(state, draftState => {
                draftState.updatingBrand = true;
                draftState.brandError = null;
            });
        case types.UPDATE_BRAND_SUCCESS:
            return produce(state, draftState => {
                draftState.updatingBrand = false;
                draftState.brands = draftState.brands.map(brand => (brand._id === payload._id ? payload : brand));
            });
        case types.UPDATE_BRAND_FAIL:
            return produce(state, draftState => {
                draftState.updatingBrand = false;
                draftState.brandError = payload;
            });

        case types.DELETE_BRAND:
            return produce(state, draftState => {
                draftState.deletingBrand = true;
                draftState.brandError = null;
            });
        case types.DELETE_BRAND_SUCCESS:
            return produce(state, draftState => {
                draftState.deletingBrand = false;
                draftState.brands = draftState.brands.filter(brand => brand._id !== payload.documentId);
            });
        case types.DELETE_BRAND_FAIL:
            return produce(state, draftState => {
                draftState.deletingBrand = false;
                draftState.brandError = payload;
            });

        // Join request actions
        case types.REQUEST_TO_JOIN_CAMPAIGN:
            return produce(state, draftState => {
                draftState.requestingToJoin = true;
                draftState.joinRequestError = null;
            });
        case types.REQUEST_TO_JOIN_CAMPAIGN_SUCCESS:
            return produce(state, draftState => {
                draftState.requestingToJoin = false;
                draftState.joinRequests = [...draftState.joinRequests, payload];
            });
        case types.REQUEST_TO_JOIN_CAMPAIGN_FAIL:
            return produce(state, draftState => {
                draftState.requestingToJoin = false;
                draftState.joinRequestError = payload;
            });

        case types.APPROVE_CAMPAIGN_JOIN_REQUEST:
            return produce(state, draftState => {
                draftState.approvingRequest = true;
                draftState.joinRequestError = null;
            });
        case types.APPROVE_CAMPAIGN_JOIN_REQUEST_SUCCESS:
            return produce(state, draftState => {
                draftState.approvingRequest = false;
                draftState.joinRequests = draftState.joinRequests.map(request =>
                    request.id === payload.requestId ? { ...request, status: 'approved' } : request,
                );
            });
        case types.APPROVE_CAMPAIGN_JOIN_REQUEST_FAIL:
            return produce(state, draftState => {
                draftState.approvingRequest = false;
                draftState.joinRequestError = payload;
            });

        case types.REJECT_CAMPAIGN_JOIN_REQUEST:
            return produce(state, draftState => {
                draftState.rejectingRequest = true;
                draftState.joinRequestError = null;
            });
        case types.REJECT_CAMPAIGN_JOIN_REQUEST_SUCCESS:
            return produce(state, draftState => {
                draftState.rejectingRequest = false;
                draftState.joinRequests = draftState.joinRequests.map(request =>
                    request.id === payload.requestId ? { ...request, status: 'rejected' } : request,
                );
            });
        case types.REJECT_CAMPAIGN_JOIN_REQUEST_FAIL:
            return produce(state, draftState => {
                draftState.rejectingRequest = false;
                draftState.joinRequestError = payload;
            });

        // Message actions
        case types.SEND_CAMPAIGN_MESSAGE:
            return produce(state, draftState => {
                draftState.sendingMessage = true;
                draftState.messageError = null;
            });
        case types.SEND_CAMPAIGN_MESSAGE_SUCCESS:
            return produce(state, draftState => {
                draftState.sendingMessage = false;
                draftState.messages = [...draftState.messages, payload];
            });
        case types.SEND_CAMPAIGN_MESSAGE_FAIL:
            return produce(state, draftState => {
                draftState.sendingMessage = false;
                draftState.messageError = payload;
            });

        case types.DELETE_CAMPAIGN_MESSAGE:
            return produce(state, draftState => {
                draftState.deletingMessage = true;
                draftState.messageError = null;
            });
        case types.DELETE_CAMPAIGN_MESSAGE_SUCCESS:
            return produce(state, draftState => {
                draftState.deletingMessage = false;
                draftState.messages = draftState.messages.filter(message => message.id !== payload.messageId);
            });
        case types.DELETE_CAMPAIGN_MESSAGE_FAIL:
            return produce(state, draftState => {
                draftState.deletingMessage = false;
                draftState.messageError = payload;
            });

        case types.UPDATE_CAMPAIGN_MESSAGE:
            return produce(state, draftState => {
                draftState.updatingMessage = true;
                draftState.messageError = null;
            });
        case types.UPDATE_CAMPAIGN_MESSAGE_SUCCESS:
            return produce(state, draftState => {
                draftState.updatingMessage = false;
                draftState.messages = draftState.messages.map(message =>
                    message.id === payload.id ? payload : message,
                );
            });
        case types.UPDATE_CAMPAIGN_MESSAGE_FAIL:
            return produce(state, draftState => {
                draftState.updatingMessage = false;
                draftState.messageError = payload;
            });

        // Deliverable actions
        case types.REQUEST_DELIEVERABLE_UPDATE:
            return produce(state, draftState => {
                draftState.requestingUpdate = true;
                draftState.deliverableError = null;
            });
        case types.REQUEST_DELIEVERABLE_UPDATE_SUCCESS:
            return produce(state, draftState => {
                draftState.requestingUpdate = false;
                draftState.deliverables = [...draftState.deliverables, payload];
            });
        case types.REQUEST_DELIEVERABLE_UPDATE_FAIL:
            return produce(state, draftState => {
                draftState.requestingUpdate = false;
                draftState.deliverableError = payload;
            });

        case types.APPROVE_DELIVERABLE_SUBMISSION:
            return produce(state, draftState => {
                draftState.approvingDeliverable = true;
                draftState.deliverableError = null;
            });
        case types.APPROVE_DELIVERABLE_SUBMISSION_SUCCESS:
            return produce(state, draftState => {
                draftState.approvingDeliverable = false;
                draftState.deliverables = draftState.deliverables.map(deliverable =>
                    deliverable.id === payload.deliverableId ? { ...deliverable, status: 'approved' } : deliverable,
                );
            });
        case types.APPROVE_DELIVERABLE_SUBMISSION_FAIL:
            return produce(state, draftState => {
                draftState.approvingDeliverable = false;
                draftState.deliverableError = payload;
            });

        case types.UPLOAD_DELIVERABLE:
            return produce(state, draftState => {
                draftState.uploadingDeliverable = true;
                draftState.deliverableError = null;
            });
        case types.UPLOAD_DELIVERABLE_SUCCESS:
            return produce(state, draftState => {
                draftState.uploadingDeliverable = false;
                draftState.deliverables = [...draftState.deliverables, payload];
            });
        case types.UPLOAD_DELIVERABLE_FAIL:
            return produce(state, draftState => {
                draftState.uploadingDeliverable = false;
                draftState.deliverableError = payload;
            });

        // Post scheduling actions
        case types.SCHEDULE_POST:
            return produce(state, draftState => {
                draftState.schedulingPost = true;
                draftState.scheduleError = null;
            });
        case types.SCHEDULE_POST_SUCCESS:
            return produce(state, draftState => {
                draftState.schedulingPost = false;
                draftState.scheduledPosts = [...draftState.scheduledPosts, payload];
            });
        case types.SCHEDULE_POST_FAIL:
            return produce(state, draftState => {
                draftState.schedulingPost = false;
                draftState.scheduleError = payload;
            });
        case types.GET_BUZZBRIDGE_PROFILE:
            return produce(state, draftState => {
                if (!payload.skipRefresh) {
                    draftState.gettingBuzzProfile = true;
                    draftState.buzzProfile = undefined;
                }
            });
        case types.GET_BUZZBRIDGE_PROFILE_SUCCESS:
            return produce(state, draftState => {
                draftState.gettingBuzzProfile = false;
                draftState.buzzProfile = payload;
            });
        case types.GET_BUZZBRIDGE_PROFILE_FAIL:
            return produce(state, draftState => {
                draftState.gettingBuzzProfile = false;
                draftState.error = payload;
            });
        case types.UPDATE_BUZZBRIDGE_PROFILE_SUCCESS:
            return produce(state, draftState => {
                const { updatedFields } = payload || {};
                Object.keys(payload).forEach(key => {
                    const typedKey = key as keyof IInfluencer;
                    if (
                        updatedFields &&
                        updatedFields.includes(typedKey) &&
                        draftState.buzzProfile &&
                        draftState.buzzProfile.data.influencer
                        //     ||
                        // (!_.isEqual(payload[typedKey], draftState.buzzProfile?.data.influencer[typedKey]) &&
                        //     draftState.buzzProfile &&
                        //     draftState.buzzProfile.data.influencer)
                    ) {
                        draftState.buzzProfile = {
                            ...draftState.buzzProfile,
                            data: {
                                ...draftState.buzzProfile.data,
                                influencer: {
                                    ...draftState.buzzProfile.data.influencer,
                                    [typedKey]: payload[typedKey],
                                },
                            },
                        };
                    }
                });
            });
        case 'LOGOUT_SUCCESS':
            return initialState;
        default:
            return state;
    }
};

export { reducer };
